import { useEffect, useMemo, useState } from 'react';
import { Pencil, Plus, Search, Star, Trash2 } from 'lucide-react';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { Button } from '../../../shared/components/Button';
import { EmptyState } from '../../../shared/components/EmptyState';
import { Textarea } from '../../../shared/components/Textarea';
import { TextInput } from '../../../shared/components/TextInput';
import { useToast } from '../../../shared/components/Toast';
import { maskPhone } from '../../../shared/utils/validators';
import { useEstados, useMunicipios } from '../../geo/hooks';
import { mensagemDeErro, statusDoErro } from '../api';
import { AddressPayload, Contact, ContactKind, EmergencyContact, EmergencyPayload, Owner, consultarCep } from '../api/cadastro';
import {
  useAprovarEndereco, useContatos, useCriarEndereco, useEnderecos, useRejeitarEndereco, useRemoverContato,
  useRemoverEmergencia, useSalvarContato, useSalvarEmergencia,
} from '../hooks/cadastro';
import { ADDRESS_STATUS_LABELS, CONTACT_KIND_LABELS, formatCep, formatDate } from './format';
import { PessoasModal } from './PessoasModal';

interface Props {
  owner: Owner;
  /** DP com `pessoas.enderecos-contatos` em `create`/`edit`, ou o próprio colaborador. */
  canEdit: boolean;
  /** DP com `pessoas.alteracoes-cadastrais`: aprova endereços enviados pelo portal. */
  canApprove?: boolean;
}

/** Endereços com vigência, contatos e contatos de emergência (US-GP-003). */
export function EnderecosContatosTab({ owner, canEdit, canApprove = false }: Props) {
  const toast = useToast();
  const enderecos = useEnderecos(owner);
  const contatos = useContatos(owner);
  const aprovar = useAprovarEndereco();
  const rejeitar = useRejeitarEndereco();
  const removerContato = useRemoverContato(owner);
  const removerEmergencia = useRemoverEmergencia(owner);
  const [addressOpen, setAddressOpen] = useState(false);
  const [contact, setContact] = useState<{ open: boolean; item: Contact | null }>({ open: false, item: null });
  const [emergency, setEmergency] = useState<{ open: boolean; item: EmergencyContact | null }>({ open: false, item: null });
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  async function run(action: () => Promise<unknown>, ok: string, fail: string) {
    try {
      await action();
      toast.success(ok);
    } catch (err) {
      toast.error(mensagemDeErro(err, fail));
    }
  }

  const portal = owner.kind === 'me';

  return (
    <div className="space-y-6">
      <section className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-800">Endereços</h3>
          {canEdit && (
            <Button size="sm" onClick={() => setAddressOpen(true)}>
              <Plus className="mr-1 h-4 w-4" /> {portal ? 'Informar novo endereço' : 'Novo endereço'}
            </Button>
          )}
        </div>
        {portal && <p className="text-xs text-gray-500">Um novo endereço passa a valer depois da aprovação do Departamento Pessoal.</p>}
        {(enderecos.data ?? []).length === 0 ? (
          <EmptyState size="compact" title="Nenhum endereço cadastrado" />
        ) : (
          <ul className="divide-y divide-gray-100 rounded-md border border-gray-200">
            {(enderecos.data ?? []).map((a) => (
              <li key={a.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
                <div>
                  <p className="font-medium text-gray-900">
                    {a.type === 'residencial' ? 'Residencial' : 'Correspondência'} · {ADDRESS_STATUS_LABELS[a.status]}
                  </p>
                  <p className="text-gray-700">
                    {a.street}, {a.number}{a.complement ? ` — ${a.complement}` : ''} · {a.district} · {a.cityName}/{a.stateUf} · CEP {formatCep(a.zipCode)}
                  </p>
                  <p className="text-xs text-gray-500">
                    Vigência: {formatDate(a.validFrom)} a {a.validTo ? formatDate(a.validTo) : 'atual'}
                    {a.rejectionReason ? ` · Rejeitado: ${a.rejectionReason}` : ''}
                  </p>
                </div>
                {a.status === 'pendente' && canApprove && (
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => run(() => aprovar.mutateAsync(a.id), 'Endereço aprovado.', 'Não foi possível aprovar.')}>
                      Aprovar
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => { setReason(''); setRejecting(a.id); }}>Rejeitar</Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-800">Contatos</h3>
          {canEdit && (
            <Button size="sm" variant="secondary" onClick={() => setContact({ open: true, item: null })}>
              <Plus className="mr-1 h-4 w-4" /> Novo contato
            </Button>
          )}
        </div>
        {(contatos.data?.contacts ?? []).length === 0 ? (
          <p className="text-sm text-gray-500">Nenhum contato adicional.</p>
        ) : (
          <ul className="space-y-1 text-sm">
            {(contatos.data?.contacts ?? []).map((c) => (
              <li key={c.id} className="flex items-center justify-between">
                <span>
                  {CONTACT_KIND_LABELS[c.kind]}: {c.value}
                  {c.isPrimary && <Star className="ml-1 inline h-3.5 w-3.5 text-amber-500" aria-label="Principal" />}
                </span>
                {canEdit && (
                  <span className="flex gap-1">
                    <button type="button" className="p-1 text-gray-500" onClick={() => setContact({ open: true, item: c })} aria-label="Editar"><Pencil className="h-4 w-4" /></button>
                    <button type="button" className="p-1 text-gray-500" aria-label="Remover"
                      onClick={() => run(() => removerContato.mutateAsync(c.id), 'Contato removido.', 'Não foi possível remover.')}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-800">Contatos de emergência</h3>
          {canEdit && (
            <Button size="sm" variant="secondary" onClick={() => setEmergency({ open: true, item: null })}>
              <Plus className="mr-1 h-4 w-4" /> Novo contato de emergência
            </Button>
          )}
        </div>
        {(contatos.data?.emergencyContacts ?? []).length === 0 ? (
          <p className="text-sm text-gray-500">Nenhum contato de emergência.</p>
        ) : (
          <ul className="space-y-1 text-sm">
            {(contatos.data?.emergencyContacts ?? []).map((c) => (
              <li key={c.id} className="flex items-center justify-between">
                <span>
                  {c.name} ({c.relationship}) · {[c.mobile, c.phone, c.email].filter(Boolean).join(' · ')}
                  {c.isPrimary && <Star className="ml-1 inline h-3.5 w-3.5 text-amber-500" aria-label="Principal" />}
                </span>
                {canEdit && (
                  <span className="flex gap-1">
                    <button type="button" className="p-1 text-gray-500" onClick={() => setEmergency({ open: true, item: c })} aria-label="Editar"><Pencil className="h-4 w-4" /></button>
                    <button type="button" className="p-1 text-gray-500" aria-label="Remover"
                      onClick={() => run(() => removerEmergencia.mutateAsync(c.id), 'Contato removido.', 'Não foi possível remover.')}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <AddressModal owner={owner} open={addressOpen} onClose={() => setAddressOpen(false)}
        onSaved={(m) => { setAddressOpen(false); toast.success(m); }} />
      <ContactModal owner={owner} state={contact} onClose={() => setContact({ open: false, item: null })}
        onSaved={(m) => { setContact({ open: false, item: null }); toast.success(m); }} />
      <EmergencyModal owner={owner} state={emergency} onClose={() => setEmergency({ open: false, item: null })}
        onSaved={(m) => { setEmergency({ open: false, item: null }); toast.success(m); }} />
      <PessoasModal
        open={rejecting !== null}
        title="Rejeitar endereço"
        submitLabel="Rejeitar"
        submitVariant="danger"
        canSubmit={reason.trim().length > 0}
        isSubmitting={rejeitar.isPending}
        onClose={() => setRejecting(null)}
        onSubmit={() => rejecting && run(async () => {
          await rejeitar.mutateAsync({ id: rejecting, reason: reason.trim() });
          setRejecting(null);
        }, 'Endereço rejeitado.', 'Não foi possível rejeitar.')}
      >
        <Textarea label="Motivo" value={reason} onChange={(e) => setReason(e.target.value)} />
      </PessoasModal>
    </div>
  );
}

const EMPTY_ADDRESS: AddressPayload = { type: 'residencial', zipCode: '', street: '', number: '', district: '', stateUf: '', cityId: 0 };

function AddressModal({ owner, open, onClose, onSaved }: { owner: Owner; open: boolean; onClose: () => void; onSaved: (m: string) => void }) {
  const criar = useCriarEndereco(owner);
  const [value, setValue] = useState<AddressPayload>(EMPTY_ADDRESS);
  const [error, setError] = useState<string | null>(null);
  const [cepInfo, setCepInfo] = useState<string | null>(null);
  const estados = useEstados();
  const municipios = useMunicipios(value.stateUf || null);
  const cidades = useMemo(
    () => (municipios.data ?? []).slice().sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR')),
    [municipios.data],
  );

  useEffect(() => {
    if (!open) return;
    setValue(EMPTY_ADDRESS);
    setError(null);
    setCepInfo(null);
  }, [open]);

  async function buscarCep() {
    setCepInfo(null);
    try {
      const r = await consultarCep(value.zipCode);
      setValue((v) => ({
        ...v, street: r.street || v.street, district: r.district || v.district, stateUf: r.stateUf || v.stateUf,
        cityId: r.cityId ?? v.cityId, complement: v.complement || r.complement || undefined,
      }));
    } catch (err) {
      setCepInfo(statusDoErro(err) === 404 ? 'CEP não encontrado.' : 'Consulta de CEP indisponível. Preencha manualmente.');
    }
  }

  async function submit() {
    if (value.zipCode.replace(/\D/g, '').length !== 8) return setError('Informe o CEP com 8 dígitos.');
    if (!value.street || !value.number || !value.district || !value.stateUf || !value.cityId) {
      return setError('Preencha logradouro, número, bairro, UF e município.');
    }
    try {
      const saved = await criar.mutateAsync({ ...value, zipCode: value.zipCode.replace(/\D/g, '') });
      onSaved(saved.status === 'pendente' ? 'Endereço enviado para aprovação.' : 'Endereço cadastrado.');
    } catch (err) {
      setError(mensagemDeErro(err, 'Não foi possível salvar o endereço.'));
    }
  }

  return (
    <PessoasModal open={open} size="lg" title="Endereço" submitLabel="Salvar" isSubmitting={criar.isPending}
      submitError={error} onClose={onClose} onSubmit={submit}>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <AutocompleteSelect
          label="Tipo"
          options={[{ value: 'residencial', label: 'Residencial' }, { value: 'correspondencia', label: 'Correspondência' }]}
          value={value.type}
          onChange={(v) => setValue((x) => ({ ...x, type: v as AddressPayload['type'] }))}
        />
        <TextInput
          label="CEP"
          value={formatCep(value.zipCode)}
          onChange={(e) => setValue((x) => ({ ...x, zipCode: e.target.value }))}
          onBlur={() => value.zipCode.replace(/\D/g, '').length === 8 && buscarCep()}
          rightElement={
            <button type="button" onClick={buscarCep} className="text-gray-500" aria-label="Buscar CEP"><Search className="h-4 w-4" /></button>
          }
        />
        <TextInput label="Logradouro" value={value.street} onChange={(e) => setValue((x) => ({ ...x, street: e.target.value }))} />
        <TextInput label="Número" value={value.number} onChange={(e) => setValue((x) => ({ ...x, number: e.target.value }))} />
        <TextInput label="Complemento" value={value.complement ?? ''} onChange={(e) => setValue((x) => ({ ...x, complement: e.target.value }))} />
        <TextInput label="Bairro" value={value.district} onChange={(e) => setValue((x) => ({ ...x, district: e.target.value }))} />
        <AutocompleteSelect
          label="UF"
          options={(estados.data ?? []).map((e) => ({ value: e.uf, label: `${e.uf} — ${e.nome}` }))}
          value={value.stateUf}
          onChange={(v) => setValue((x) => ({ ...x, stateUf: v, cityId: 0 }))}
        />
        <AutocompleteSelect
          label="Município"
          disabled={!value.stateUf}
          options={cidades.map((m) => ({ value: String(m.idIbge), label: m.nome }))}
          value={value.cityId ? String(value.cityId) : ''}
          onChange={(v) => setValue((x) => ({ ...x, cityId: Number(v) }))}
        />
        {owner.kind === 'dp' && (
          <TextInput label="Início da vigência" type="date" value={value.validFrom ?? ''}
            onChange={(e) => setValue((x) => ({ ...x, validFrom: e.target.value || undefined }))} />
        )}
      </div>
      {cepInfo && <p className="text-xs text-amber-700">{cepInfo}</p>}
    </PessoasModal>
  );
}

function ContactModal({ owner, state, onClose, onSaved }: {
  owner: Owner; state: { open: boolean; item: Contact | null }; onClose: () => void; onSaved: (m: string) => void;
}) {
  const salvar = useSalvarContato(owner);
  const [kind, setKind] = useState<ContactKind>('telefone');
  const [value, setValue] = useState('');
  const [primary, setPrimary] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!state.open) return;
    setKind(state.item?.kind ?? 'telefone');
    setValue(state.item?.value ?? '');
    setPrimary(state.item?.isPrimary ?? false);
    setError(null);
  }, [state]);

  async function submit() {
    try {
      await salvar.mutateAsync({ id: state.item?.id ?? null, payload: { kind, value: value.trim(), isPrimary: primary } });
      onSaved('Contato salvo.');
    } catch (err) {
      setError(mensagemDeErro(err, 'Não foi possível salvar o contato.'));
    }
  }

  return (
    <PessoasModal open={state.open} title="Contato" submitLabel="Salvar" isSubmitting={salvar.isPending} submitError={error}
      onClose={onClose} onSubmit={submit}>
      <AutocompleteSelect
        label="Tipo"
        options={Object.entries(CONTACT_KIND_LABELS).map(([v, l]) => ({ value: v, label: l }))}
        value={kind}
        onChange={(v) => setKind(v as ContactKind)}
      />
      <TextInput
        label={kind === 'email_pessoal' ? 'E-mail' : 'Número'}
        value={value}
        onChange={(e) => setValue(kind === 'email_pessoal' ? e.target.value : maskPhone(e.target.value))}
      />
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={primary} onChange={(e) => setPrimary(e.target.checked)} /> Principal deste tipo</label>
    </PessoasModal>
  );
}

function EmergencyModal({ owner, state, onClose, onSaved }: {
  owner: Owner; state: { open: boolean; item: EmergencyContact | null }; onClose: () => void; onSaved: (m: string) => void;
}) {
  const salvar = useSalvarEmergencia(owner);
  const [value, setValue] = useState<EmergencyPayload>({ name: '', relationship: '' });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!state.open) return;
    const i = state.item;
    setValue(i
      ? { name: i.name, relationship: i.relationship, phone: i.phone, mobile: i.mobile, email: i.email, notes: i.notes, isPrimary: i.isPrimary }
      : { name: '', relationship: '' });
    setError(null);
  }, [state]);

  async function submit() {
    if (!value.name.trim() || !value.relationship.trim()) return setError('Informe nome e parentesco.');
    if (!value.phone && !value.mobile && !value.email) return setError('Informe ao menos um telefone, celular ou e-mail.');
    try {
      await salvar.mutateAsync({ id: state.item?.id ?? null, payload: value });
      onSaved('Contato de emergência salvo.');
    } catch (err) {
      setError(mensagemDeErro(err, 'Não foi possível salvar o contato.'));
    }
  }

  return (
    <PessoasModal open={state.open} title="Contato de emergência" submitLabel="Salvar" isSubmitting={salvar.isPending}
      submitError={error} onClose={onClose} onSubmit={submit}>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <TextInput label="Nome" value={value.name} onChange={(e) => setValue((v) => ({ ...v, name: e.target.value }))} />
        <TextInput label="Parentesco" value={value.relationship} onChange={(e) => setValue((v) => ({ ...v, relationship: e.target.value }))} />
        <TextInput label="Celular" value={value.mobile ?? ''} onChange={(e) => setValue((v) => ({ ...v, mobile: maskPhone(e.target.value) || null }))} />
        <TextInput label="Telefone" value={value.phone ?? ''} onChange={(e) => setValue((v) => ({ ...v, phone: maskPhone(e.target.value) || null }))} />
        <TextInput label="E-mail" value={value.email ?? ''} onChange={(e) => setValue((v) => ({ ...v, email: e.target.value || null }))} />
      </div>
      <Textarea label="Observações" value={value.notes ?? ''} onChange={(e) => setValue((v) => ({ ...v, notes: e.target.value || null }))} />
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={!!value.isPrimary} onChange={(e) => setValue((v) => ({ ...v, isPrimary: e.target.checked }))} />
        Contato principal
      </label>
    </PessoasModal>
  );
}
